// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable camelcase */
import { useMemo, useRef, useState } from 'react';

import { useFileEncryptionUpload } from '@/entities/file/hooks/use-file-encryption-upload';
import { useNotices } from '@/features/notices/useNotices';
import { JobStatus } from '@/features/smartImport/types';
import { ApiAssetExtractUriItem, ApiJobDocument, useApi } from '@/shared/api';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { useProfileStore } from '@/shared/store/profile.store';

import { ExtractParams } from '../types';

const POLLING_INTERVAL_MS = 2 * 1000;

export const useAiExtraction = () => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobDoc, setJobDoc] = useState<ApiJobDocument | null>(null);
  const { encryptAndUploadFile, deleteUploadedFile } = useFileEncryptionUpload();
  const notices = useNotices();

  const api = useApi();
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const { isBeneficiary, isAssistant } = useProfileStore();
  const sjEncryptedFileKey = useSjActions('encryptedFileKey');

  return useMemo(() => {
    const startPolling = (onReady: (job: ApiJobDocument) => void, onField: () => void) => {
      if (!jobId) return;
      const pollJob = async () => {
        const { data: jobData, error } = await api.aiGetJob(jobId);

        if (error) {
          notices.error('error' in error ? String(error.error) : error.message);
          onField();
          stopPolling();
        }

        if (!jobData) return;
        onReady(jobData);
      };

      if (intervalId.current) return;
      intervalId.current = setInterval(pollJob, POLLING_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (!intervalId.current) return;
      clearInterval(intervalId.current);
      intervalId.current = null;

      const fileIds = jobDoc?.meta.file_ids;
      if (!(Array.isArray(fileIds) && fileIds.some((id) => typeof id === 'string'))) return;

      deleteFiles(fileIds);
    };

    const extractFiles = async ({ files, kind, subtype }: ExtractParams) => {
      const filePaths = await Promise.all(
        files.map((file) => encryptAndUploadFile(file, { withoutMetaInfo: true })),
      );

      const items = filePaths.reduce<ApiAssetExtractUriItem[]>((acc, path) => {
        const encrypted = sjEncryptedFileKey?.findOne(({ id }) => id.includes(path));
        if (!encrypted) return acc;

        const data: ApiAssetExtractUriItem = {
          file_id: encrypted.id,
          content_uri: path,
          symmetric_key: encrypted?.symmetricKey,
        };

        acc.push(data);
        return acc;
      }, []);

      const { data: batchData } = await api.aiExtractEntityByUri({
        items,
        kind,
        subtype_hint: subtype,
      });
      if (!batchData) return;

      setJobId(batchData.job_id);
    };

    const deleteFiles = async (encryptedFileKeyIds: string[]) => {
      return await Promise.allSettled(encryptedFileKeyIds.map((id) => deleteUploadedFile(id)));
    };

    const resetStates = () => {
      setJobId(null);
      setJobDoc(null);
    };

    const fetchPendingJob = async () => {
      if (isBeneficiary || isAssistant) return;
      const { data } = await api.aiGetJobs();
      const pendingJob = data?.jobs?.find((job) => job.status === JobStatus.PENDING);
      setJobId(pendingJob?.job_id ?? null);
    };

    const runJob = async (id: string | null, onField: () => void) => {
      if (!id) return;

      const { data: initialJobData } = await api.aiGetJob(id);
      if (!initialJobData) return;
      setJobDoc(initialJobData);

      if (initialJobData.status === JobStatus.DONE) {
        resetStates();
        return;
      }

      startPolling((data) => {
        if (data.done === jobDoc?.done) return;
        setJobDoc(data);
      }, onField);
    };

    return {
      deleteFiles,
      startPolling,
      stopPolling,
      extractFiles,
      resetStates,
      fetchPendingJob,
      runJob,
      jobId,
      jobDoc,
    };
  }, [deleteUploadedFile, encryptAndUploadFile, api, isBeneficiary, isAssistant, jobId, jobDoc]);
};
